import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import {
  Shield,
  MessageSquare,
  Repeat,
  Heart,
  BookOpen,
  Users,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Helmet } from "react-helmet";
const BoundariesInRelationships = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = {
    overview: "Overview",
    types: "Types of Boundaries",
    benefits: "Benefits",
    challenges: "Common Challenges",
  };

  const boundaryExamples = [
    {
      type: "Physical",
      examples: [
        "I need personal space when I'm feeling overwhelmed",
        "I prefer asking before sharing food or drinks",
        "I'm comfortable with hugs but not with unexpected touches",
      ],
    },
    {
      type: "Emotional",
      examples: [
        "I need time to process my emotions before discussing them",
        "Please don't pressure me to share feelings when I'm not ready",
        "I value emotional support without immediate advice",
      ],
    },
    {
      type: "Digital",
      examples: [
        "I prefer not to share my location constantly",
        "I'd like to keep some conversations private",
        "I need uninterrupted offline time regularly",
      ],
    },
    {
      type: "Time",
      examples: [
        "I need to schedule alone time for self-care",
        "Please respect my work hours for non-emergencies",
        "I value maintaining my individual friendships and hobbies",
      ],
    },
  ];

  // Load AdSense script dynamically
  useEffect(() => {
    const script = document.createElement("script");
    document.title = "Boundaries in Relationships | Relationship Wellness";
    script.src =
      "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset.adClient = "ca-pub-2067522743161930";
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-indigo-50 to-white">
      <Header />
      <Helmet>
        <title>Boundaries in Relationships</title>
        <meta
          name="description"
          content="Article about having boundaries in Relationships."
        />
      </Helmet>
      {/* Top Banner Ad */}
      <div className="container mx-auto px-10 py-6">
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2067522743161930"
          data-ad-slot="YYYYYYYYYY"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      </div>

      <main className="flex-grow container mx-auto px-10 py-12 max-w-5xl relative">
        {/* Sidebar Ad (visible on medium screens and up) */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold text-blue-800 mb-6">
            Setting Healthy Boundaries
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Healthy boundaries are the invisible lines that define where you end
            and others begin. They create the foundation for mutual respect,
            emotional safety, and authentic connection in relationships.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex p-1 rounded-xl bg-gray-100">
            {Object.entries(tabs).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-6 py-3 text-lg rounded-lg transition-all duration-200 ${
                  activeTab === key
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-200"
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Floating Skyscraper Ad (Visible on Large Screens) */}
        <div className="hidden lg:block fixed right-4 top-1/4 w-160px">
          <ins
            className="adsbygoogle"
            style={{ display: "inline-block", width: "160px", height: "600px" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="0987654321" // Replace with actual slot ID
            data-ad-format="auto"></ins>
          <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Boundaries define what behaviors are acceptable to you and how you
              expect to be treated. They're essential for maintaining your
              identity, protecting your well-being, and nurturing healthy
              relationships. When boundaries are clear and respected,
              relationships flourish with mutual trust and understanding.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Why Boundaries Matter
                </h3>
                <p className="text-gray-700">
                  Boundaries help you maintain a strong sense of self while
                  being in relationship with others. They create clarity about
                  what's okay and what's not, reducing misunderstandings and
                  conflict. Without healthy boundaries, relationships can become
                  sources of stress, resentment, and emotional exhaustion.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Signs of Weak Boundaries
                </h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Feeling responsible for others' emotions</li>
                  <li>• Difficulty saying "no" without guilt</li>
                  <li>• Overcommitting or people-pleasing</li>
                  <li>• Feeling drained after social interactions</li>
                  <li>• Sharing too much, too soon with new people</li>
                  <li>• Tolerating disrespectful or hurtful behavior</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100">
              <h2 className="text-3xl font-bold text-blue-800 mb-6">
                Essential Steps to Setting Healthy Boundaries
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-600 rounded-xl text-white mt-1">
                    <Shield size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-800">
                      1. Self-Awareness
                    </h3>
                    <p className="text-gray-700">
                      Reflect deeply on your values, needs, emotions, and
                      comfort levels. Notice when you feel uncomfortable,
                      resentful, or drained in situations—these are often signs
                      of boundary violations.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-600 rounded-xl text-white mt-1">
                    <MessageSquare size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-800">
                      2. Clear Communication
                    </h3>
                    <p className="text-gray-700">
                      Express your boundaries directly and respectfully. Use
                      specific "I" statements that focus on your needs rather
                      than criticizing the other person. Be concise and avoid
                      over-explaining.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-600 rounded-xl text-white mt-1">
                    <Repeat size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-800">
                      3. Consistency
                    </h3>
                    <p className="text-gray-700">
                      Follow through on your boundaries consistently. When you
                      waver, others learn your boundaries are negotiable.
                      Consistency builds trust and teaches others how to respect
                      your limits.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-600 rounded-xl text-white mt-1">
                    <Heart size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-800">
                      4. Practice Empathy
                    </h3>
                    <p className="text-gray-700">
                      Understand and respect others' boundaries as you would
                      want yours respected. Relationship boundaries work best
                      when they're reciprocal and built on mutual understanding.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-600 rounded-xl text-white mt-1">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-800">
                      5. Regular Reassessment
                    </h3>
                    <p className="text-gray-700">
                      Revisit your boundaries periodically. As you grow and
                      relationships evolve, your boundaries may need
                      adjustments. Healthy relationships can accommodate these
                      changes.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-600 rounded-xl text-white mt-1">
                    <Users size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-800">
                      6. Seek Support
                    </h3>
                    <p className="text-gray-700">
                      If setting or maintaining boundaries feels challenging,
                      consider working with a therapist or counselor. Support
                      groups can also provide guidance and encouragement.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* AdSense Rectangular Ad - Mid-Content */}
            <div className="my-8">
              <ins
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client="ca-pub-2067522743161930"
                data-ad-slot="5678901234" // Replace with actual slot ID
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
              <script>
                (adsbygoogle = window.adsbygoogle || []).push({});
              </script>
            </div>
          </div>
        )}

        {/* Types of Boundaries Tab */}
        {activeTab === "types" && (
          <div className="space-y-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Boundaries come in many forms, each protecting different aspects
              of your well-being. Understanding the various types can help you
              identify areas where you may need to establish or strengthen
              boundaries.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Physical Boundaries
                </h3>
                <p className="text-gray-700 mb-4">
                  Physical boundaries involve your personal space, privacy, and
                  body. They define your comfort level with physical touch,
                  proximity to others, and how your physical space is respected.
                </p>
                <p className="text-gray-600 italic">
                  "I'm not comfortable with hugs from people I've just met."
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Emotional Boundaries
                </h3>
                <p className="text-gray-700 mb-4">
                  Emotional boundaries protect your right to your own feelings
                  and emotional energy. They involve separating your emotions
                  from others' and maintaining emotional privacy when needed.
                </p>
                <p className="text-gray-600 italic">
                  "I need some time to process this before discussing it
                  further."
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Time Boundaries
                </h3>
                <p className="text-gray-700 mb-4">
                  Time boundaries protect how you allocate your time and energy.
                  They help you maintain balance between relationships, work,
                  personal interests, and rest.
                </p>
                <p className="text-gray-600 italic">
                  "I'm not available after 8pm as that's my personal time."
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Digital Boundaries
                </h3>
                <p className="text-gray-700 mb-4">
                  Digital boundaries govern your online presence, communication
                  expectations, and technology use within relationships. They're
                  increasingly important in our connected world.
                </p>
                <p className="text-gray-600 italic">
                  "I don't share my location with partners or check my messages
                  during work hours."
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Material Boundaries
                </h3>
                <p className="text-gray-700 mb-4">
                  Material boundaries relate to your money, possessions, and
                  resources. They clarify what you're willing to share, lend, or
                  give to others.
                </p>
                <p className="text-gray-600 italic">
                  "I'm not comfortable lending money to friends."
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Intellectual Boundaries
                </h3>
                <p className="text-gray-700 mb-4">
                  Intellectual boundaries protect your thoughts, ideas, and
                  beliefs. They involve respecting differences in opinion and
                  avoiding attempts to force your views on others.
                </p>
                <p className="text-gray-600 italic">
                  "I'm open to discussion, but please don't dismiss my
                  viewpoint."
                </p>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="text-2xl font-bold text-blue-800 mb-6">
                Example Boundary Statements
              </h3>
              <div className="space-y-6">
                {boundaryExamples.map((category, index) => (
                  <div
                    key={index}
                    className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                    <h4 className="text-xl font-semibold text-blue-800 mb-3">
                      {category.type} Boundary Examples
                    </h4>
                    <ul className="space-y-2">
                      {category.examples.map((example, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-blue-600 mr-2">•</span>
                          <span className="text-gray-700">"{example}"</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Benefits Tab */}
        {activeTab === "benefits" && (
          <div className="space-y-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Setting and maintaining healthy boundaries creates profound
              positive effects in your relationships and personal wellbeing.
              Here are the transformative benefits of establishing clear
              boundaries.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Shield className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-blue-800 mb-2">
                  Increased Self-Respect
                </h3>
                <p className="text-gray-700">
                  When you set boundaries, you communicate to yourself and
                  others that your needs matter. This builds self-esteem and
                  personal dignity.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <MessageSquare className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-blue-800 mb-2">
                  Clearer Communication
                </h3>
                <p className="text-gray-700">
                  Boundaries reduce misunderstandings by explicitly stating
                  expectations and needs, creating transparent and honest
                  relationships.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Heart className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-blue-800 mb-2">
                  Deeper Intimacy
                </h3>
                <p className="text-gray-700">
                  Contrary to what some fear, healthy boundaries actually foster
                  greater emotional intimacy by creating safety for authentic
                  connection.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <BookOpen className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-blue-800 mb-2">
                  Better Self-Understanding
                </h3>
                <p className="text-gray-700">
                  The process of setting boundaries requires self-reflection,
                  helping you better understand your values, needs, and
                  triggers.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <AlertCircle className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-blue-800 mb-2">
                  Reduced Resentment
                </h3>
                <p className="text-gray-700">
                  When boundaries are clear, you're less likely to feel taken
                  advantage of or overlooked, preventing the build-up of
                  resentment.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Users className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-blue-800 mb-2">
                  Healthier Relationships
                </h3>
                <p className="text-gray-700">
                  Relationships with clear boundaries tend to be more balanced,
                  respectful, and mutually satisfying for all involved.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100">
              <h3 className="text-2xl font-bold text-blue-800 mb-4">
                Real-Life Impact
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-lg font-semibold text-blue-800 mb-2">
                    Before Boundaries
                  </h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Feeling overwhelmed by others' demands</li>
                    <li>• Experiencing burnout from overcommitting</li>
                    <li>• Resentment building in relationships</li>
                    <li>• Unclear expectations leading to conflicts</li>
                    <li>• Difficulty identifying your own needs</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-lg font-semibold text-blue-800 mb-2">
                    After Establishing Boundaries
                  </h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Greater sense of personal agency</li>
                    <li>• More energy for things that matter to you</li>
                    <li>• More authentic and satisfying connections</li>
                    <li>• Clarity about your role in relationships</li>
                    <li>• Increased respect from others</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Challenges Tab */}
        {activeTab === "challenges" && (
          <div className="space-y-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Setting and maintaining boundaries isn't always easy.
              Understanding common challenges can help you navigate
              boundary-setting more effectively and with greater confidence.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Fear of Rejection
                </h3>
                <p className="text-gray-700 mb-3">
                  Many people worry that setting boundaries will cause others to
                  dislike or abandon them. This fear can make it difficult to
                  assert your needs.
                </p>
                <h4 className="text-lg font-semibold text-blue-700 mb-2">
                  How to Overcome:
                </h4>
                <p className="text-gray-700">
                  Remember that healthy relationships thrive on honest
                  communication. People who truly care about you will respect
                  your boundaries, even if there's initial adjustment needed.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Guilt and People-Pleasing
                </h3>
                <p className="text-gray-700 mb-3">
                  If you've been conditioned to prioritize others' needs, you
                  may feel guilty when putting yourself first by setting
                  boundaries.
                </p>
                <h4 className="text-lg font-semibold text-blue-700 mb-2">
                  How to Overcome:
                </h4>
                <p className="text-gray-700">
                  Recognize that setting boundaries is not selfish—it's
                  necessary for your wellbeing and for the health of your
                  relationships. Start with smaller boundaries and build
                  confidence.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Resistance from Others
                </h3>
                <p className="text-gray-700 mb-3">
                  Some people may resist or push back when you establish new
                  boundaries, especially if they benefited from the lack of
                  them.
                </p>
                <h4 className="text-lg font-semibold text-blue-700 mb-2">
                  How to Overcome:
                </h4>
                <p className="text-gray-700">
                  Stay firm but compassionate. Explain your boundaries clearly
                  but don't feel obligated to defend them. Give others time to
                  adjust, but be consistent in enforcement.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">
                  Difficulty Identifying Boundaries
                </h3>
                <p className="text-gray-700 mb-3">
                  If you've rarely had boundaries before, you might struggle to
                  identify what boundaries you need or what feels right for you.
                </p>
                <h4 className="text-lg font-semibold text-blue-700 mb-2">
                  How to Overcome:
                </h4>
                <p className="text-gray-700">
                  Pay attention to your emotions—discomfort, resentment, and
                  anxiety often signal boundary violations. Journal about
                  situations that leave you feeling drained or upset to identify
                  patterns.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100">
              <h3 className="text-2xl font-bold text-blue-800 mb-6">
                Boundary Scripts for Difficult Situations
              </h3>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-3">
                    When Someone Keeps Asking After You've Said No
                  </h4>
                  <p className="text-gray-700 italic mb-2">
                    "I understand this is important to you, but my answer is
                    still no. I need you to respect my decision."
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-3">
                    When Work Encroaches on Personal Time
                  </h4>
                  <p className="text-gray-700 italic mb-2">
                    "I'm not available after working hours except for genuine
                    emergencies. I can address this first thing tomorrow
                    morning."
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-3">
                    When Someone Shares Too Much Personal Information
                  </h4>
                  <p className="text-gray-700 italic mb-2">
                    "I appreciate your trust, but I'm not comfortable discussing
                    this topic. Could we talk about something else?"
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-3">
                    When You Need Space in a Relationship
                  </h4>
                  <p className="text-gray-700 italic mb-2">
                    "I care about you and our relationship. I also need some
                    alone time to recharge. It's not about you—it's something I
                    need for my wellbeing."
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16 bg-blue-800 text-white p-8 rounded-2xl">
          <h2 className="text-3xl font-bold mb-4" style={{color:"#FFF"}}>Remember</h2>
          <p className="text-xl">
            Setting boundaries is an act of self-care and respect—both for
            yourself and your relationships. It takes practice and courage, but
            the rewards of healthier connections and increased wellbeing are
            well worth the effort.
          </p>
        </div>

        {/* AdSense Banner Ad - Before Footer */}
        <div className="my-8">
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="4321098765" // Replace with actual slot ID
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
          <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BoundariesInRelationships;
